import React, { useMemo } from 'react';

// Reutiliza os estilos do card privado para manter consistência visual
import '../pages/RespostaQuestao.css';
import { useModal } from '../hooks/useModal';
import { FaQuestionCircle } from 'react-icons/fa';
import { isEvidenciaObrigatoria } from '../utils/evidencias';




const getFase = (percentual) => {
  if (percentual >= 70) return { texto: 'Aprimorado', classe: 'aprimorado' };
  if (percentual >= 40) return { texto: 'Intermediário', classe: 'intermediario' };
  if (percentual >= 15) return { texto: 'Inicial', classe: 'inicial' };
  return { texto: 'Inexpressivo', classe: 'inexpressivo' };
};

const normalizaItem = (item) => (typeof item === 'object' && item !== null ? item.texto : item);

const RespostaCardPublic = ({ questao }) => {
  const { showModal } = useModal();
  const resposta = questao && questao.resposta ? questao.resposta : {};

  const percentual = useMemo(() => {
    const p = parseFloat(resposta.pontuacao);
    if (isNaN(p)) return 0;
    // No público, pontuação vem como fração (0..1); converte para %
    return Math.round(p * 100);
  }, [resposta.pontuacao]);

  const fase = getFase(percentual);

  const mostraTratamentoRisco = useMemo(() => {
    const principal = (resposta.resposta_principal || '').toLowerCase();
    return principal.startsWith('não se aplica') || principal.startsWith('nao se aplica');
  }, [resposta.resposta_principal]);

  const evidenciasObrigatorias = useMemo(
    () => isEvidenciaObrigatoria(resposta.resposta_principal || ''),
    [resposta.resposta_principal]
  );

  const notaQuestoesAdicionais = evidenciasObrigatorias ? 'Recomendado' : 'Opcional';

  const itensAdicionais = Array.isArray(questao?.questoes_adicionais)
    ? questao.questoes_adicionais.map(normalizaItem).filter(Boolean)
    : [];

  const selecionados = new Set(Array.isArray(resposta.questoes_adicionais) ? resposta.questoes_adicionais : []);

  const handleShowGuidance = () => {
    if (!questao.texto_orientativo) return;
    showModal({
      title: `Orientações: Questão ${questao.codigo}`,
      message: <div style={{ whiteSpace: 'pre-wrap' }}>{questao.texto_orientativo}</div>,
      confirmText: 'Entendido',
      onConfirm: () => { }
    });
  };

  const priorizacaoClass = questao?.priorizacao
    ? `priority-${questao.priorizacao.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '-')}`
    : 'priority-none';

  return (
    <div className="public-visualizer" style={{ display: 'flex', flexDirection: 'row', minHeight: 'auto', height: 'auto', border: 'none', boxShadow: 'none', width: '100%' }}>

      {/* SIDEBAR DE PRIORIDADE */}
      <div className={`page-priority-sidebar ${priorizacaoClass}`} style={{ width: '30px', minHeight: 'auto' }}>
        <span className="priority-vertical-text" style={{ fontSize: '0.75rem' }}>{questao.priorizacao || ''}</span>
      </div>

      <div className="question-content-scroller" style={{ padding: '1.5rem 1.5rem 0.5rem 1.5rem', overflow: 'visible', width: '100%' }}>

        {/* HEADER COM STATUS E PONTUAÇÃO */}
        <div className="question-header-modern" style={{ marginBottom: '1.5rem' }}>
          <div className="header-top-stats" style={{ paddingBottom: '1rem', marginBottom: '1rem', gap: '1.5rem' }}>
            <div className="adoption-status-group">
              <span className={`adoption-value ${fase.classe}`} style={{ fontSize: '2.5rem', color: 'var(--primary-color)' }}>{percentual}%</span>
              <span className={`adoption-label ${fase.classe}`} style={{ color: '#64748b' }}>{fase.texto}</span>
            </div>

            <div style={{ flexGrow: 1 }}>
              <div className="horizontal-progress-track">
                <div className={`horizontal-progress-fill ${fase.classe}`} style={{ width: `${percentual}%`, height: '100%', borderRadius: '20px' }}></div>
              </div>
              <div className="header-meta-info" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                <span>{questao ? (questao.eixo || '-') : '-'} &gt; <strong>#{questao.codigo}</strong></span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
            <h1 style={{ fontSize: '1.25rem', color: '#1e293b', fontWeight: '700', lineHeight: '1.4', margin: 0 }}>
              {questao ? (questao.enunciado || '') : ''}
            </h1>
            {questao?.texto_orientativo && (
              <button
                type="button"
                className="minimal-back-btn"
                onClick={handleShowGuidance}
                title="Ver orientações"
                style={{ whiteSpace: 'nowrap' }}
              >
                <FaQuestionCircle /> Ajuda
              </button>
            )}
          </div>
        </div>

        {/* FORMS DE RESPOSTA */}
        <div className="resposta-form" style={{ marginTop: '0' }}>

          <div className="form-section">
            <h2 style={{ fontSize: '1rem', color: '#475569', marginBottom: '0.5rem' }}>Resposta Principal</h2>
            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontWeight: '500', color: '#334155' }}>
              {resposta.resposta_principal || 'Não informado'}
            </div>
          </div>

          {mostraTratamentoRisco && (
            <div className="form-section conditional-section" style={{ marginTop: '1.5rem' }}>
              <h2 style={{ fontSize: '1rem', color: '#475569', marginBottom: '0.5rem' }}>Tratamento do Risco da Não Aplicabilidade</h2>
              <div style={{ background: '#fff7ed', padding: '1rem', borderRadius: '8px', border: '1px solid #ffedd5', color: '#9a3412' }}>
                {resposta.tratamento_risco || 'Não informado'}
              </div>
            </div>
          )}

          {itensAdicionais.length > 0 && (
            <div className="form-section" style={{ marginTop: '1.5rem' }}>
              <h2 style={{ fontSize: '1rem', color: '#475569', marginBottom: '0.5rem' }}>
                Itens de Verificação <span className="field-note" style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#94a3b8' }}>({notaQuestoesAdicionais})</span>
              </h2>
              <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {itensAdicionais.map((texto) => (
                  <label key={texto} className="checkbox-label" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.9rem', color: '#334155' }}>
                    <input type="checkbox" checked={selecionados.has(texto)} readOnly disabled style={{ marginTop: '0.2rem' }} />
                    <span>{texto}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default RespostaCardPublic;

